// Page: Templates
// Purpose: App page for browsing graphic and (optionally) mail templates with
// search, tag filters, saved filter, infinite scroll, and per-namespace saved
// state. Includes preview modal and plan-based upgrade nudges.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import TemplateSearch from '../../components/templates/templates/TemplateSearch';
import { Tooltip } from '../../components/ui/overlays/Tooltip';
import TemplateFilters from '../../components/templates/templates/TemplateFilters';
import TemplateGrid from '../../components/templates/templates/TemplateGrid';
import TemplateModal from '../../components/templates/templates/TemplateModal';
import MailTemplateModal from '../../components/templates/mail/MailTemplateModal';
import { getTemplates } from '../../api/content/templatesApi';
import { saveTemplate, unsaveTemplate, getSavedTemplateIds } from '../../api/content/templateActionsApi';
import { useAuth } from '../../context/auth'; // Import auth context
import ContactInfoModal from '../../components/ui/modals/contact-info-modal.jsx';
import usersApi from '../../api/accounts/usersApi';
import { useNamespace } from '../../context/namespace'; // Import namespace context
import { useToast } from '../../components/ui/notifications/toast-notification.jsx'; // Import toast notifications if available
import Footer from '../../components/shared/Footer';
import { AlertCircle, TrendingUp, Clock, DollarSign } from 'lucide-react';
import { BASIC_TEMPLATE_PAGE_LIMIT, TEMPLATES_PER_PAGE } from '../../data/limits/templateLimits';
import PremiumUpgradeNotice from '../../components/ui/notifications/premium-upgrade-notice.jsx';
import { TAG_LIST, MAIL_TEMPLATE_TAG_LIST } from '../../data/tags/tagEnums';
import SEOHead from '../../components/shared/SEOHead';

// Add keyframe animation for fade-in
const fadeInAnimation = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;


function Templates() {
  
  // Add the style tag for animations
  useEffect(() => {
    // Add the animation styles to the document head
    const styleTag = document.createElement('style');
    styleTag.innerHTML = fadeInAnimation;
    document.head.appendChild(styleTag);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  // State declarations - moved before useEffect hooks that depend on them
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateType, setTemplateType] = useState('graphics');

  // Set up responsive skeleton columns
  useEffect(() => {
    const updateSkeletonColumns = () => {
      const width = window.innerWidth;
      if (templateType === 'mail') {
        // Mail: 2 columns below 1280px, 3 columns at >= 1280px
        if (width < 1280) {
          setSkeletonColumns(2);
        } else {
          setSkeletonColumns(3);
        }
      } else {
        // Graphics/default: 2 columns below 1024px, 3 columns 1024–1279px, 4 at >=1280px
        if (width < 1024) {
          setSkeletonColumns(2);
        } else if (width < 1280) {
          setSkeletonColumns(3);
        } else {
          setSkeletonColumns(4);
        }
      }
    };

    // Initial setup
    updateSkeletonColumns();

    // Update on resize
    window.addEventListener('resize', updateSkeletonColumns);
    return () => window.removeEventListener('resize', updateSkeletonColumns);
  }, [templateType]); // Add templateType as dependency
  const [sortMethod, setSortMethod] = useState('random');
  const [showSavedTemplates, setShowSavedTemplates] = useState(false); // State for saved templates
  const [savedTemplateIds, setSavedTemplateIds] = useState(new Set()); // Track which templates are saved
  const [savedTemplatesLoaded, setSavedTemplatesLoaded] = useState(false); // Track if saved templates have been loaded
  const [loadingSavedTemplates, setLoadingSavedTemplates] = useState(false);

  // State for API data and pagination
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [allTemplatesLoaded, setAllTemplatesLoaded] = useState(false);
  const [skeletonColumns, setSkeletonColumns] = useState(4); // State for responsive skeleton columns
  
  // Get auth context and toast
  const { user, setUser } = useAuth();
  // Contact info modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [initialPhone, setInitialPhone] = useState(null);
  const [initialRole, setInitialRole] = useState('');

  useEffect(() => {
    if (!user) return;
    // show only when profile seems loaded (has planLevel/email at least)
    const profileReady = user.email && (user.planLevel || user.subscriptionStatus);
    if (!profileReady) return;
    const hasPhone = !!(user.phone?.e164 || user.phone?.national);
    const hasRole = !!(user.role && String(user.role).trim());
    const hasSeen = user.hasSeenContactInfoModal === true;
    if ((!hasPhone || !hasRole) && !hasSeen) {
      setInitialPhone(user.phone || { e164: '', national: '', country: 'US' });
      setInitialRole(user.role || '');
      setShowContactModal(true);
    }
  }, [user]);

  const closeContactModal = async () => {
    try {
      await usersApi.updateContactInfo({ hasSeenContactInfoModal: true });
      if (typeof setUser === 'function') {
        setUser(prev => prev ? { ...prev, hasSeenContactInfoModal: true } : prev);
      }
    } catch (_) {}
    setShowContactModal(false);
  };

  const submitContactInfo = async ({ role, phone }) => {
    await usersApi.updateContactInfo({ role, phone, hasSeenContactInfoModal: true });
    if (typeof setUser === 'function') {
      setUser(prev => prev ? { ...prev, role, phone, hasSeenContactInfoModal: true } : prev);
    }
    setShowContactModal(false);
  };
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Get namespace context
  const { namespaces, currentNamespace } = useNamespace();
  
  // Find the current namespace ID from the namespaces array
  const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  // Get user's plan
  const userPlan = user?.subscriptionStatus?.currentPlanLevel || user?.planLevel || 'basic';
  const isPremiumUser = userPlan === 'premium';
  const isBasicUser = userPlan === 'basic';
  
  // We'll use this to track if basic user has reached their page limit
  const hasReachedBasicPageLimit = isBasicUser && page >= BASIC_TEMPLATE_PAGE_LIMIT;
  
  // Refs for scroll handling and API request tracking
  
  const lastLoadTimeRef = useRef(0);
  const apiRequestInProgressRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  const requestCountRef = useRef(0);
  
  // We don't need the debounced search anymore since we're using a search button
  

  // Add intersection observer refs
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  // Active filters converted to array format for API
  const activeFilters = useMemo(() => {
    return Array.isArray(activeFilter) 
      ? activeFilter 
      : (activeFilter ? [activeFilter] : []);
  }, [activeFilter]);

  // Convert UI sort method to API searchType
  const getSearchType = (method) => {
    switch(method) {
      case 'newest': return 'newlyAdded';
      case 'popular': return 'mostPopular';
      case 'random': return 'random';
      default: return 'random';
    }
  };

  // Convert UI template type to API type parameter
  const getApiTemplateType = (uiType) => {
    switch(uiType) {
      case 'graphics': return 'graphic';
      case 'websites': return 'website';
      case 'mail': return 'mail';
      default: return '';
    }
  };

  // Function to handle save/unsave template
  const handleSaveTemplate = useCallback(async (templateId, isSaved) => {
    if (!user) {
      addToast('You must be logged in to save templates', 'error');
      return;
    }

    if (!currentNamespaceId) {
      addToast('You must select a namespace to save templates', 'error');
      return;
    }

    // Optimistically update the UI
    if (isSaved) {
      // Template is already saved, mark as unsaved in UI
      setSavedTemplateIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(templateId);
        return newSet;
      });
    } else {
      // Template is not saved, mark as saved in UI
      setSavedTemplateIds(prev => new Set([...prev, templateId]));
    }
    
    try {
      if (isSaved) {
        // Template is already saved, unsave it
        await unsaveTemplate(templateId, currentNamespaceId);
      } else {
        // Template is not saved, save it
        await saveTemplate(templateId, currentNamespaceId);
      }
      
      // If we're viewing saved templates, refresh the list
      if (showSavedTemplates) {
        loadInitialTemplates(searchQuery, sortMethod, templateType, true);
      }
    } catch (error) {
      console.error('Error toggling save status:', error);
      addToast(error.message || 'Failed to update saved status', 'error');
      
      // Revert the UI change on error
      if (isSaved) {
        // If unsaving failed, add back to saved templates
        setSavedTemplateIds(prev => new Set([...prev, templateId]));
      } else {
        // If saving failed, remove from saved templates
        setSavedTemplateIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(templateId);
          return newSet;
        });
      }
    }
  }, [user, currentNamespaceId, showSavedTemplates, searchQuery, sortMethod, templateType, addToast]);

  // Function to toggle saved templates filter
  const handleToggleSavedFilter = useCallback(() => {
    // If trying to see saved templates but no namespace is selected
    if (!showSavedTemplates && !currentNamespaceId) {
      addToast('Please select a namespace to view saved templates', 'warning');
      return;
    }
    
    const newValue = !showSavedTemplates;
    setShowSavedTemplates(newValue);
    
    // When turning off the saved filter, we need to revert to the actual saved state
    // rather than assuming all templates are saved
    if (!newValue) {
      // Refresh saved template IDs from the backend
      if (user && currentNamespaceId) {
        getSavedTemplateIds(currentNamespaceId).then(savedIds => {
          console.log(`[Templates] Refreshing saved template IDs when disabling saved filter:`, savedIds);
          setSavedTemplateIds(new Set(savedIds || []));
        }).catch(err => {
          console.error('[Templates] Error refreshing saved template IDs:', err);
          // On error, empty the set to avoid false positives
          setSavedTemplateIds(new Set());
        });
      } else {
        // If no user or no namespace, clear saved templates
        setSavedTemplateIds(new Set());
      }
    }
    
    // Reset pagination when saved filter changes
    setPage(1);
    setHasMore(true);
    setAllTemplatesLoaded(false);
  }, [showSavedTemplates, user, currentNamespaceId, addToast]);

  // Function to handle sort method change
  const handleSortChange = useCallback((method) => {
    console.log(`[Templates] Sort method changed to: ${method}`);
    setSortMethod(method);
    // Reset pagination when sort method changes
    setPage(1);
    setHasMore(true);
    setAllTemplatesLoaded(false);
  }, []);

  // Function to handle template type change
  const handleTemplateTypeChange = useCallback((type) => {
    console.log(`[Templates] Template type changed to: ${type}`);
    
    // Check available types
    const availableTypes = ['graphics', 'mail'];
    
    if (!availableTypes.includes(type)) {
      // For now, websites and other templates are coming soon 
      addToast('This template type is coming soon!', 'info');
      return;
    }
    
    setTemplateType(type);
    // Each tab opens in its default sort: mail newest-first, graphics randomized
    setSortMethod(type === 'mail' ? 'newest' : 'random');
    // Reset pagination when template type changes
    setPage(1);
    setHasMore(true);
    setAllTemplatesLoaded(false);
  }, [addToast]);

  // Function to handle search button click
  const handleSearch = useCallback((query) => {
    console.log(`[Templates] Search button clicked with query: "${query}"`);
    // Only trigger loading state if the search query actually changed
    if (query !== searchQuery) {
      setSearchQuery(query);
      // Reset pagination when search query changes
      setPage(1);
      setHasMore(true);
      setAllTemplatesLoaded(false);
      
      // Use the separate search loading state
      setSearchLoading(true);
    }
  }, [searchQuery]);

  // New method that will be called when clear search is clicked via TemplateSearch component
  const handleClearSearch = useCallback(() => {
    console.log('[Templates] Clear search button clicked');
    setSearchQuery('');
    setPage(1);
    setHasMore(true);
    setAllTemplatesLoaded(false);
  }, []);

  // Fetch saved templates when user logs in or component mounts or namespace changes
  useEffect(() => {
    const loadSavedTemplateIds = async () => {
      if (user && currentNamespaceId) {
        // Prevent multiple simultaneous calls to the same namespace
        const cacheKey = `saved_templates_${currentNamespaceId}`;
        const isAlreadyFetching = sessionStorage.getItem(`${cacheKey}_fetching`) === 'true';
        
        if (isAlreadyFetching) {
          console.log(`[Templates] Already fetching saved templates for namespace ${currentNamespaceId}, skipping`);
          return;
        }
        
        // Check if we have recent cached data
        const cachedData = sessionStorage.getItem(cacheKey);
        const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
        const now = Date.now();
        
        if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 30000) { // 30 second cache
          console.log(`[Templates] Using cached saved templates for namespace ${currentNamespaceId}`);
          const savedIds = JSON.parse(cachedData);
          setSavedTemplateIds(new Set(savedIds));
          setSavedTemplatesLoaded(true);
          return;
        }
        
        setLoadingSavedTemplates(true);
        sessionStorage.setItem(`${cacheKey}_fetching`, 'true');
        
        try {
          console.log(`[Templates] Fetching saved templates for namespace: ${currentNamespace} (${currentNamespaceId})`);
          const savedIds = await getSavedTemplateIds(currentNamespaceId);
          console.log(`[Templates] Found ${savedIds.length} saved templates in namespace:`, savedIds);
          
          // Cache the results
          sessionStorage.setItem(cacheKey, JSON.stringify(savedIds || []));
          sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
          
          if (savedIds && savedIds.length > 0) {
            setSavedTemplateIds(new Set(savedIds));
          } else {
            setSavedTemplateIds(new Set());
          }
          
          // Track that we've made the initial attempt to load saved templates
          setSavedTemplatesLoaded(true);
        } catch (err) {
          console.error('[Templates] Error loading saved templates:', err);
          // Set the flag anyway so we don't keep trying on errors
          setSavedTemplatesLoaded(true);
          setSavedTemplateIds(new Set());
        } finally {
          setLoadingSavedTemplates(false);
          sessionStorage.removeItem(`${cacheKey}_fetching`);
        }
      } else {
        // Clear saved templates when logged out or no namespace
        setSavedTemplateIds(new Set());
        setSavedTemplatesLoaded(true);
      }
    };

    loadSavedTemplateIds();
  }, [user, currentNamespace, currentNamespaceId]); // Depend on user and namespace

  // Keep track of whether we've made the first API call
  const hasInitializedRef = useRef(false);

  // Update the initial loading useEffect - SINGLE CONSOLIDATED EFFECT
  useEffect(() => {
    // Skip API calls in these cases:
    // 1. If we've already started an API request
    if (apiRequestInProgressRef.current) {
      console.log('[Templates] ⏭️ Skipping useEffect trigger - API request in progress');
      setSearchLoading(false);
      return;
    }
    
    // 2. If we're actively loading saved templates (wait for that to finish first)
    if (loadingSavedTemplates && user) {
      console.log('[Templates] ⏭️ Skipping useEffect trigger - waiting for saved templates to load');
      setSearchLoading(false);
      return;
    }
    
    // 3. Special case: If this is the very first load, we want to make sure savedTemplatesLoaded is true
    //    UNLESS there's no user (in which case we should load right away)
    if (!hasInitializedRef.current && user && !savedTemplatesLoaded) {
      console.log('[Templates] ⏭️ Skipping initial load - waiting for saved templates data first');
      setSearchLoading(false);
      return;
    }
    
    console.log('[Templates] 🚀 Loading templates - trigger:', {
      hasInitialized: hasInitializedRef.current,
      savedTemplatesLoaded,
      activeFilters,
      currentNamespaceId
    });
    
    // Actually load templates
    loadInitialTemplates().finally(() => {
      // Clear search loading state if it was set
      setSearchLoading(false);
    });
    
    // Mark that we've initialized
    hasInitializedRef.current = true;
  }, [activeFilters, loadingSavedTemplates, user, currentNamespaceId, savedTemplatesLoaded, sortMethod, templateType, searchQuery, showSavedTemplates]);

  // Function to load initial templates
  const loadInitialTemplates = useCallback(async (
    searchQueryParam = searchQuery, 
    sortMethodParam = sortMethod, 
    templateTypeParam = templateType,
    showSavedParam = showSavedTemplates
  ) => {
    // Add timestamp check to avoid calls too close together
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    const MIN_TIME_BETWEEN_CALLS = 500; // Reduced to 500ms for better responsiveness
    
    if (timeSinceLastLoad < MIN_TIME_BETWEEN_CALLS && lastLoadTimeRef.current > 0) {
      console.log(`[Templates] 🛑 Throttling API call - last request was only ${timeSinceLastLoad}ms ago`);
      return Promise.resolve();
    }

    // Don't make API call if:
    // 1. We're already loading
    // 2. The API request is already in progress
    // 3. We're still loading saved templates (unless this is a manual search/filter change)
    const isAutoLoad = arguments.length === 0;
    if (
      loading || 
      apiRequestInProgressRef.current || 
      (isAutoLoad && loadingSavedTemplates && !savedTemplatesLoaded)
    ) {
      console.log('[Templates] ⏭️ Skipping initial load - loading in progress or waiting for saved templates');
      // Clear search loading state since we're not making a request
      setSearchLoading(false);
      return Promise.resolve(); // Return a resolved promise when skipping
    }
    
    // If requesting saved templates but user is not logged in, show an error
    if (showSavedParam && !user) {
      setError('You must be logged in to view saved templates');
      setTemplates([]);
      setInitialLoading(false);
      setLoading(false);
      setSearchLoading(false);
      return Promise.resolve();
    }
    
    // If requesting saved templates but no namespace is selected, show an error
    if (showSavedParam && !currentNamespaceId) {
      setError('You must select a namespace to view saved templates');
      setTemplates([]);
      setInitialLoading(false);
      setLoading(false);
      setSearchLoading(false);
      return Promise.resolve();
    }
    
    // Create a unique key for this request to deduplicate
    const requestParams = JSON.stringify({
      page: 1,
      tags: activeFilters.join(','),
      search: searchQueryParam,
      sort: sortMethodParam,
      type: templateTypeParam,
      showSaved: showSavedParam,
      namespaceId: currentNamespaceId
    });
    
    // If this exact request was just made, skip it
    if (requestParams === lastRequestParamsRef.current) {
      console.log('[Templates] 🔄 Skipping duplicate request with identical parameters');
      // Clear search loading state since we're not making a request
      setSearchLoading(false);
      return Promise.resolve(); // Return a resolved promise when skipping
    }
    
    // Record the request timestamp and parameters
    lastLoadTimeRef.current = now;
    
    console.log('[Templates] 📡 Starting API request for templates:', {
      requestCount: requestCountRef.current + 1,
      tags: activeFilters.join(','),
      search: searchQueryParam,
      sort: sortMethodParam,
      type: templateTypeParam,
      showSaved: showSavedParam
    });
    
    setLoading(true);
    setInitialLoading(true);
    setError(null);
    setPage(1);
    setAllTemplatesLoaded(false);
    
    // Mark this request as in progress
    apiRequestInProgressRef.current = true;
    lastRequestParamsRef.current = requestParams;
    
    // Debug: track request count
    requestCountRef.current++;
    
    try {
      const requestOptions = {
        page: 1,
        limit: 25,
        tags: activeFilters.join(','),
        search: searchQueryParam,
        searchType: getSearchType(sortMethodParam),
        type: getApiTemplateType(templateTypeParam),
        showSavedTemplates: showSavedParam
      };
      
      // Only include namespaceId if we're showing saved templates
      if (showSavedParam && currentNamespaceId) {
        requestOptions.namespaceId = currentNamespaceId;
      }
      
      const result = await getTemplates(requestOptions);
      
      console.log(`[Templates] ✅ Received ${result.templates?.length || 0} templates`);
      setTemplates(result.templates || []);
      
      // Update saved template IDs from the result if we're viewing saved templates
      if (result.templates && result.templates.length > 0 && showSavedParam) {
        // If we're showing saved templates, all returned templates are saved
        setSavedTemplateIds(new Set(result.templates.map(t => t.id)));
      }
      
      // Check if we've loaded all templates
      if (!result.templates || result.templates.length < 25) {
        console.log('[Templates] All templates loaded (less than limit returned)');
        setHasMore(false);
        setAllTemplatesLoaded(true);
      } else {
        setHasMore(true);
      }
      
      return result; // Return the result for promise chaining
    } catch (err) {
      setError('Failed to load templates. Please try again later.');
      console.error('[Templates] Error loading templates:', err);
      throw err; // Rethrow for promise chaining
    } finally {
      setLoading(false);
      setInitialLoading(false);
      apiRequestInProgressRef.current = false;
      
      // Clear the request params after a small delay to prevent rapid duplicate calls
      setTimeout(() => {
        lastRequestParamsRef.current = null;
      }, 1000); // Reduced to 1 second
    }
  }, [activeFilters, searchQuery, sortMethod, templateType, showSavedTemplates, user, loadingSavedTemplates, savedTemplatesLoaded, currentNamespaceId]);

  // Function to load more templates (for infinite scroll)
  const loadMoreTemplates = useCallback(async () => {
    // Don't load more if:
    // 1. Already loading
    // 2. No more templates to load
    // 3. Already got all templates
    // 4. API request is already in progress
    // 5. Currently loading more (from our local ref)
    // 6. Basic user has reached their page limit
    if (
      loading || 
      !hasMore || 
      allTemplatesLoaded || 
      apiRequestInProgressRef.current || 
      loadingMoreRef.current || 
      (isBasicUser && page >= BASIC_TEMPLATE_PAGE_LIMIT)
    ) {
      console.log('[Templates] Skipping loadMore - conditions not met');
      return;
    }
    
    // Mark as loading more locally
    loadingMoreRef.current = true;
    
    setLoading(true);
    const nextPage = page + 1;
    
    // Use fixed page size of 25 for all request types
    const pageLimit = TEMPLATES_PER_PAGE;
    
    console.log(`[Templates] 🔄 Loading more templates (page ${nextPage}, limit ${pageLimit})`);
    
    // Mark this request as in progress
    apiRequestInProgressRef.current = true;
    
    try {
      const requestOptions = {
        page: nextPage,
        limit: pageLimit,
        tags: activeFilters.join(','),
        search: searchQuery,
        searchType: getSearchType(sortMethod),
        type: getApiTemplateType(templateType),
        showSavedTemplates: showSavedTemplates
      };
      
      // Only include namespaceId if we're showing saved templates
      if (showSavedTemplates && currentNamespaceId) {
        requestOptions.namespaceId = currentNamespaceId;
      }
      
      console.log('[Templates] 📡 Requesting page', nextPage, 'with options:', requestOptions);
      
      const result = await getTemplates(requestOptions);
      
      // Enhanced logging to debug empty results
      if (!result.templates || result.templates.length === 0) {
        console.warn('[Templates] ⚠️ Received empty templates array from API:', result);
        console.log('[Templates] 🛑 No more templates available (empty results)');
        setHasMore(false);
        setAllTemplatesLoaded(true);
        setLoading(false);
        return;
      }
      
      console.log(`[Templates] 📥 Received ${result.templates?.length || 0} additional templates for page ${nextPage}`);
      
      // Get set of existing template IDs to avoid duplicates
      const existingIds = new Set(templates.map(template => template.id));
      console.log(`[Templates] 🧹 Filtering out duplicates from ${result.templates.length} templates`);
      
      // Filter out any templates that are already in the list
      const newTemplates = result.templates.filter(template => !existingIds.has(template.id));
      
      console.log(`[Templates] ✅ Adding ${newTemplates.length} unique templates to list (filtered out ${result.templates.length - newTemplates.length} duplicates)`);
      
      // Only update if we have new unique templates
      if (newTemplates.length > 0) {
        // Append new templates to the existing list
        setTemplates(prev => [...prev, ...newTemplates]);
        
        // Update saved template IDs
        if (showSavedTemplates) {
          setSavedTemplateIds(prev => {
            const newSet = new Set(prev);
            newTemplates.forEach(t => newSet.add(t.id));
            return newSet;
          });
        }
        
        // Always update the page counter when we've successfully added templates
        setPage(nextPage);
        
        // For basic users, check if we're at the page limit
        if (isBasicUser && nextPage >= BASIC_TEMPLATE_PAGE_LIMIT) {
          console.log('[Templates] Basic user reached page limit, stopping pagination');
          setHasMore(false);
        }
        // Check if we've loaded all templates - if we received less than the full page size
        else if (result.templates.length < pageLimit) {
          console.log('[Templates] 🛑 All templates loaded (less than limit returned)');
          setHasMore(false);
          setAllTemplatesLoaded(true);
        }
      } else {
        // If all templates were duplicates, consider stopping
        console.log('[Templates] 🛑 All templates were duplicates - may be reaching the end');
        setHasMore(false);
        setAllTemplatesLoaded(true);
      }
    } catch (err) {
      console.error('[Templates] ❌ Error loading more templates:', err);
      setError('Failed to load more templates. ' + (err.message || ''));
    } finally {
      setLoading(false);
      apiRequestInProgressRef.current = false;
      loadingMoreRef.current = false;
      // Record load time
      lastLoadTimeRef.current = Date.now();
    }
  }, [page, loading, hasMore, activeFilters, searchQuery, sortMethod, templateType, showSavedTemplates, allTemplatesLoaded, currentNamespaceId, templates, isBasicUser]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    // No need to observe if there are no more templates or we're already loading
    if (!hasMore || allTemplatesLoaded) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        // If sentinel is visible and not already loading
        if (entries[0].isIntersecting && !loading && !apiRequestInProgressRef.current) {
          console.log('[Templates] 👁️ Sentinel element is visible, loading more templates');
          loadMoreTemplates();
        }
      },
      { 
        root: null, // Use viewport
        rootMargin: '300px 0px', // Load more when within 300px of viewport bottom
        threshold: 0.1 // 10% of the target is visible
      }
    );
    
    // If sentinel element exists, observe it
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
      console.log('[Templates] 👁️ Observer attached to sentinel element');
    }
    
    // Cleanup
    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
        console.log('[Templates] 👁️ Observer detached from sentinel element');
      }
    };
  }, [loadMoreTemplates, hasMore, allTemplatesLoaded, loading]);

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const navigateTemplate = (direction) => {
    if (!selectedTemplate || templates.length <= 1) return;
    
    // Find the current template index
    const currentIndex = templates.findIndex(t => t.id === selectedTemplate.id);
    if (currentIndex === -1) return;
    
    // Calculate the new index based on direction
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % templates.length;
    } else {
      // For previous, we add the length to handle the case when currentIndex is 0
      newIndex = (currentIndex - 1 + templates.length) % templates.length;
    }
    
    // Set the new template
    setSelectedTemplate(templates[newIndex]);
  };

  // Function to handle reset of tag filters only
  const handleReset = useCallback(() => {
    setActiveFilter(null);
  }, []);

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead 
        pageType="app-templates"
      />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Templates
          </h1>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <p>Browse and select from our 500+ collection of high-converting templates</p>
            {user && (user.totalTemplatesEdited > 0 || user.totalVideoTemplatesEdited > 0) && (
              <Tooltip 
                text={
                  <div className="space-y-3">
                    <p className="font-medium">Your Stats</p>
                    <div className="flex items-center text-sm text-purple-200 mb-1">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      <span>You've used <b>{(user.totalTemplatesEdited || 0).toLocaleString()}</b> {(user.totalTemplatesEdited || 0) === 1 ? 'template' : 'templates'} and <b>{(user.totalVideoTemplatesEdited || 0).toLocaleString()}</b> video {(user.totalVideoTemplatesEdited || 0) === 1 ? 'template' : 'templates'} from Advocacy Lab</span>
                    </div>
                    <div className="flex items-center text-sm text-purple-200 mb-1">
                      <Clock className="mr-2 h-4 w-4" />
                      <span>You've saved <b>{(user.totalTimeSaved || 0).toLocaleString()}</b> {(user.totalTimeSaved || 0) === 1 ? 'hour' : 'hours'} of design time</span>
                    </div>
                    <div className="flex items-center text-sm text-purple-200">
                      <DollarSign className="mr-2 h-4 w-4" />
                      <span>You've saved <b>${(user.totalMoneySaved || 0).toLocaleString()}</b> in design costs</span>
                    </div>
                  </div>
                }
              >
                <AlertCircle className="ml-1.5 w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </Tooltip>
            )}
          </div>
        </div>
        
        {/* Template Type Toggle */}
        <div className="mt-4 sm:mt-0">
          <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                templateType === 'graphics'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => handleTemplateTypeChange('graphics')}
            >
              Graphics
            </button>
            <button
              className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                templateType === 'mail'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              onClick={() => handleTemplateTypeChange('mail')}
            >
              Mail
            </button>
            <Tooltip text="This feature is coming soon">
              <div className="relative">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-md cursor-not-allowed opacity-60 text-gray-600 dark:text-gray-400"
                  disabled
                >
                  Logos
                </button>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      
      {/* Search Component - on its own line */}
      <div className="mb-4">
      <TemplateSearch 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery} 
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        isLoading={searchLoading}
        isBasicUser={isBasicUser}
      />
      </div>
      
      {/* Filter Component - on its own line */}
      <div className="mb-6">
      <TemplateFilters 
        activeFilter={activeFilter} 
        setActiveFilter={setActiveFilter} 
        onSortChange={handleSortChange}
        savedFilterActive={showSavedTemplates}
        onToggleSavedFilter={handleToggleSavedFilter}
        isLoggedIn={!!user}
        isBasicUser={isBasicUser}
        tagList={templateType === 'mail' ? MAIL_TEMPLATE_TAG_LIST : TAG_LIST}
      />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {/* Clean, centered loading indicator for initial loading */}
      {(initialLoading || loadingSavedTemplates) && (
        <div className="space-y-6">
          {/* Show skeleton grid immediately for better perceived performance */}
          <div className="masonry-wrapper">
            <div className="masonry-grid">
              {Array.from({ length: skeletonColumns }, (_, columnIndex) => (
                <div key={`skeleton-column-${columnIndex}`} className="masonry-column" style={{ width: `${100 / skeletonColumns}%` }}>
                  {[0, 1, 2].map(itemIndex => (
                    <div key={`skeleton-${columnIndex}-${itemIndex}`} className="masonry-item p-1">
                      <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse">
                        <div className={`w-full ${templateType === 'mail' ? 'aspect-[4/3]' : 'aspect-square'} bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-lg`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Grid Component - only show when not in initial loading state */}
      {!initialLoading && !loadingSavedTemplates && (
        <>
          {templates.length > 0 ? (
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <TemplateGrid 
                templates={templates} 
                onTemplateClick={handleTemplateClick}
                savedTemplateIds={savedTemplateIds}
                onSaveTemplate={handleSaveTemplate}
                maxColumns={templateType === 'mail' ? 3 : 4}
                isMail={templateType === 'mail'}
              />
              
              {/* Premium upgrade notice for basic users who have reached their page limit */}
              {isBasicUser && page >= BASIC_TEMPLATE_PAGE_LIMIT && (
                <PremiumUpgradeNotice 
                  className="mt-8 mb-4"
                  title="Want to see more templates?"
                  description="You've reached the limit of 50 templates for Basic users. Upgrade to Premium to access our full collection."
                />
              )}
              
              {/* Sentinel element for infinite scroll - only show if user can load more */}
              {hasMore && !allTemplatesLoaded && !(isBasicUser && page >= BASIC_TEMPLATE_PAGE_LIMIT) && (
                <div 
                  ref={sentinelRef}
                  className="h-10 my-6 flex justify-center" 
                >
                  {loading && (
                    <div className="flex justify-center items-center py-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-100 dark:border-gray-800 border-b-purple-600 dark:border-b-purple-400"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <svg 
                className="w-16 h-16 text-gray-400 mb-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {showSavedTemplates ? 'No Saved Templates' : 'No Templates Found'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md">
                {showSavedTemplates 
                  ? 'You have not saved any templates yet. Browse templates and click the star icon to save them.'
                  : 'Try adjusting your search or filter criteria to find templates.'}
              </p>
            </div>
          )}
        </>
      )}
      
      {/* Template Modal - conditionally render mail template modal or regular template modal */}
      {selectedTemplate?.type?.toLowerCase() === 'mail' ? (
        <MailTemplateModal
          template={selectedTemplate}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      ) : (
      <TemplateModal
        template={selectedTemplate}
        isOpen={isModalOpen}
        onClose={closeModal}
        onNext={() => navigateTemplate('next')}
        onPrevious={() => navigateTemplate('previous')}
        hasNavigation={templates.length > 1}
        isSaved={selectedTemplate ? savedTemplateIds.has(selectedTemplate.id) : false}
        onSaveTemplate={selectedTemplate ? 
          (isSaved) => handleSaveTemplate(selectedTemplate.id, isSaved) : 
          undefined}
        isLoggedIn={!!user}
        userPlan={user?.subscriptionStatus?.currentPlanLevel || user?.planLevel || 'basic'}
      />
      )}

      {/* Footer */}
      <Footer />

      <ContactInfoModal
        isOpen={showContactModal}
        onClose={closeContactModal}
        email={user?.email}
        initialPhone={initialPhone}
        initialRole={initialRole}
        onSubmit={submitContactInfo}
        delayMs={1000}
      />
    </div>
  );
}

export default Templates; 